import React, { useMemo, useState } from 'react';
import { useProducts } from 'hooks/useProducts';

// Editor del bloque "Products" del blog. Antes pedía teclear nombre/precio/imagen
// a mano, lo que rompía el ProductCard del shop (no había `variants`, así que
// nunca se mostraba el selector ni precio dinámico). Ahora levantamos los
// productos reales de Medusa con useProducts() y guardamos el objeto completo
// — así el ProductCard renderiza variantes, precio por variante, swatches y el
// botón "Añadir a la cesta" igual que en /shop.
export default function ProductsBlockConfig({ data, onChange }) {
  // useMemo para que React detecte estabilidad de la referencia entre renders
  // mientras data.products no cambie (evita el warning de exhaustive-deps).
  const products = useMemo(() => data.products || [], [data.products]);
  const { allProducts, isLoading } = useProducts();
  const [search, setSearch] = useState('');

  const selectedIds = useMemo(() => new Set(products.map(p => p.id)), [products]);

  const matches = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    return allProducts
      .filter(p => !selectedIds.has(p.id))
      .filter(p => (p.name || '').toLowerCase().includes(q) || (p.id || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, allProducts, selectedIds]);

  const addProduct = (product) => {
    onChange({ ...data, products: [...products, product] });
    setSearch('');
  };

  const removeProduct = (index) => {
    onChange({ ...data, products: products.filter((_, i) => i !== index) });
  };

  return (
    <>
      <div className="adminblog-field">
        <label>Section Title (optional)</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
          placeholder="Products section title"
        />
      </div>

      <div className="adminblog-field">
        <label>Columns</label>
        <select
          value={data.columns || 2}
          onChange={(e) => onChange({ ...data, columns: parseInt(e.target.value) })}
        >
          <option value={2}>2 columns</option>
          <option value={3}>3 columns</option>
        </select>
      </div>

      <div className="adminblog-field">
        <label>Products ({products.length})</label>

        {/* Lista de productos ya agregados */}
        {products.map((prod, i) => (
          <div key={prod.id || i} className="adminblog-list-item" style={{ alignItems: 'center', gap: 10 }}>
            {prod.image && (
              <img
                src={prod.image}
                alt=""
                style={{ width: 40, height: 40, objectFit: 'contain', background: '#f4f6f8', flexShrink: 0 }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {prod.name || '(sin nombre)'}
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>
                {prod.variants?.length > 0
                  ? `${prod.variants.length} variante${prod.variants.length === 1 ? '' : 's'} · desde $${Math.min(...prod.variants.map(v => v.price || 0))}`
                  : `$${prod.price || 0}`}
              </div>
            </div>
            <button
              type="button"
              className="adminblog-list-remove"
              onClick={() => removeProduct(i)}
              title="Quitar"
            >
              ×
            </button>
          </div>
        ))}

        {/* Buscador para agregar productos reales */}
        <div style={{ marginTop: 8 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isLoading ? 'Cargando productos…' : 'Buscar producto por nombre…'}
            disabled={isLoading}
          />
          {search.trim() && (
            <div
              style={{
                marginTop: 6,
                border: '1px solid #d6e0ea',
                background: '#fff',
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {matches.length === 0 ? (
                <div style={{ padding: 12, fontSize: 12, color: '#888' }}>
                  Sin resultados para "{search}"
                </div>
              ) : (
                matches.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addProduct(p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: 8,
                      background: 'none',
                      border: 'none',
                      borderBottom: '1px solid #e8edf2',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {p.image && (
                      <img
                        src={p.image}
                        alt=""
                        style={{ width: 32, height: 32, objectFit: 'contain', background: '#f4f6f8', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {p.variants?.length > 0 ? `${p.variants.length} variantes` : `$${p.price}`}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: '#5b7a94' }}>+ Agregar</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
